/**
 * Web stand-in for `react-native-linear-gradient`.
 *
 * The real package dispatches on Platform.OS and, for anything that is not
 * iOS or Android, falls through to a Windows *native* view - which does not
 * exist in a browser. It is used by the sell screen product list, gift cards,
 * the payment summary and the skeleton loader, so it needs a real web
 * implementation rather than a stub.
 *
 * CSS gradients map onto the native props cleanly: `colors` + `locations`
 * become colour stops, and the `start`/`end` unit vectors become an angle.
 */

import React from 'react';
import {View} from 'react-native';

export type LinearGradientPoint = {x: number; y: number};

export type LinearGradientProps = {
  colors: (string | number)[];
  locations?: number[];
  start?: LinearGradientPoint;
  end?: LinearGradientPoint;
  useAngle?: boolean;
  angle?: number;
  style?: any;
  children?: React.ReactNode;
  [key: string]: any;
};

const DEFAULT_START: LinearGradientPoint = {x: 0.5, y: 0};
const DEFAULT_END: LinearGradientPoint = {x: 0.5, y: 1};

/**
 * Converts the native start/end unit vectors to a CSS angle.
 *
 * CSS measures from "to top" clockwise, while the RN points describe a vector
 * in a top-left origin space, so the y delta is used directly and the result
 * is normalised into 0-360.
 */
function toCssAngle(start: LinearGradientPoint, end: LinearGradientPoint): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (dx === 0 && dy === 0) {
    return 180;
  }
  const degrees = (Math.atan2(dx, dy) * 180) / Math.PI;
  return (degrees + 360) % 360;
}

function toColorStops(colors: (string | number)[], locations?: number[]): string {
  return colors
    .map((color, index) => {
      const value = typeof color === 'number' ? `#${color.toString(16).padStart(6, '0')}` : color;
      const stop = locations && locations[index] != null ? ` ${locations[index] * 100}%` : '';
      return `${value}${stop}`;
    })
    .join(', ');
}

export const LinearGradient = ({
  colors,
  locations,
  start = DEFAULT_START,
  end = DEFAULT_END,
  useAngle,
  angle,
  style,
  children,
  ...rest
}: LinearGradientProps) => {
  const resolvedAngle = useAngle && typeof angle === 'number' ? angle : toCssAngle(start, end);
  const backgroundImage = `linear-gradient(${resolvedAngle}deg, ${toColorStops(colors, locations)})`;

  // react-native-web passes unknown style keys through to the DOM node, so
  // `backgroundImage` reaches CSS untouched.
  return (
    <View {...rest} style={[style, {backgroundImage}]}>
      {children}
    </View>
  );
};

export default LinearGradient;
